import { z } from 'zod';
import {
  IataSchema,
  MiceOrderViewSchema,
  MicePhaseCountsSchema,
  MicePhaseFilterSchema,
  MicePhaseSchema,
  type MiceOrderStatus,
  type MiceOrderView,
  type MicePhase,
  type MicePhaseFilter,
} from './mice';
import { MiceColorSchema, type MiceColor } from './mice_decision';
import {
  MICE_NEED_ALERT_CODES,
  MiceNeedViewSchema,
  RECOMMENDATION_ERROR_CODES,
  type MiceNeedStatus,
  type MiceNeedView,
} from './mice_needs';
import { profileNeedsAction, type MiceProfileCheckView } from './mice_profile';

export const MiceLegDirectionSchema = z.enum(['OUTWARD', 'RETURN', 'BOTH']);
export type MiceLegDirection = z.infer<typeof MiceLegDirectionSchema>;

export const MiceNextActionSchema = z.enum([
  'recommend',
  'approve',
  'review',
  'reconsider',
  'buy',
  'retry',
  'verify',
  'wait',
  'none',
  'complete_profile',
  'details',
]);
export type MiceNextAction = z.infer<typeof MiceNextActionSchema>;

export const MiceTravelerViewSchema = z.object({
  /** `need.id`; `order:<orderId>` para pedido de planilha sem necessidade. */
  id: z.string(),
  rowNumber: z.number().int().nonnegative(),
  name: z.string().nullable(),
  email: z.string(),
  need: MiceNeedViewSchema.nullable(),
  /** Ida, depois volta, depois ida e volta. */
  orders: z.array(z.object({ order: MiceOrderViewSchema, direction: MiceLegDirectionSchema })),
  phase: MicePhaseSchema,
  nextAction: MiceNextActionSchema,
});
export type MiceTraveler = z.infer<typeof MiceTravelerViewSchema>;

/**
 * Filtro de alerta: um código, `none` (sem alerta na recomendação vigente), `profile_pending`
 * (cadastro do viajante a resolver) ou `any`.
 */
export const MiceAlertFilterSchema = z.union([
  z.enum(MICE_NEED_ALERT_CODES),
  z.literal('any'),
  z.literal('none'),
  z.literal('profile_pending'),
]);
export type MiceAlertFilter = z.infer<typeof MiceAlertFilterSchema>;

export const MiceTravelersQuerySchema = z.object({
  phase: MicePhaseFilterSchema.default('all'),
  q: z.string().trim().max(120).optional(),
  /** E-mail exato do viajante (sem caixa): o painel de um viajante fora do filtro. A busca livre
   *  fica no `q`; o `email` é a chave que o log mascara (o `q` iria inteiro para o log). */
  email: z.string().trim().toLowerCase().max(254).optional(),
  alert: MiceAlertFilterSchema.default('any'),
  /** Cor da recomendação vigente; quem não tem recomendação fica de fora de qualquer cor. */
  color: z.union([MiceColorSchema, z.literal('any')]).default('any'),
  /** IATA de origem, ou `any`. */
  origin: z.union([IataSchema, z.literal('any')]).default('any'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(100),
});
export type MiceTravelersQuery = z.infer<typeof MiceTravelersQuerySchema>;

export const MiceTravelersPageSchema = z.object({
  items: z.array(MiceTravelerViewSchema),
  /** Viajantes que batem a busca e a fase. */
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  /** Por fase, sobre os que batem a busca, o alerta e a origem (antes do filtro de fase e do de cor). */
  counts: MicePhaseCountsSchema,
  /**
   * Por cor, sobre os que batem a busca, o alerta e a origem e estão em "Para emitir". Ignora o
   * filtro de cor (como `counts` ignora o de fase): escolher uma cor não zera as outras opções.
   */
  colorCounts: z.object({
    green: z.number().int().nonnegative(),
    yellow: z.number().int().nonnegative(),
    red: z.number().int().nonnegative(),
  }),
  /** Origens de todos os viajantes do evento: as opções do filtro de origem. */
  origins: z.array(z.string()),
});
export type MiceTravelersPage = z.infer<typeof MiceTravelersPageSchema>;

// Ordinal, não localeCompare: IATA é ASCII maiúsculo, a ordem é a do sort nu.
const byCodeUnit = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

const ATTENTION_NEED: ReadonlySet<MiceNeedStatus> = new Set(['invalid', 'duplicate']);
/** Com o operador em "Para emitir": a sugestão a decidir, a aprovada e a sem solução. */
const APPROVAL_NEED: ReadonlySet<MiceNeedStatus> = new Set([
  'recommended',
  'approved',
  'no_solution',
]);
const ATTENTION_ORDER: ReadonlySet<MiceOrderStatus> = new Set([
  'invalid',
  'duplicate',
  'failed',
  'indeterminate',
]);
const BUYING_ORDER: ReadonlySet<MiceOrderStatus> = new Set([
  'queued',
  'purchasing',
  'awaiting_approval',
  'awaiting_issuance',
]);
/** Pedido que não comprou nada: o sentido pode ser comprado de novo (`isDeadNeedOrder` no mice-api). */
const DEAD_ORDER: ReadonlySet<MiceOrderStatus> = new Set(['failed', 'invalid', 'duplicate']);

/**
 * `orderIds` só cresce no servidor: uma necessidade que saiu de `ordered` (reconsiderada ou manual)
 * ainda aponta para os pedidos mortos da compra anterior. Eles são histórico, não a jornada atual.
 */
const isHistory = (need: MicePhaseNeed, order: MicePhaseOrder): boolean =>
  need.status !== 'ordered' && DEAD_ORDER.has(order.status);

/** O mínimo de uma necessidade para saber a fase do viajante (a contagem lê só isto do banco). */
export type MicePhaseNeed = Pick<
  MiceNeedView,
  'id' | 'status' | 'orderIds' | 'error' | 'profileCheck' | 'travelerEmail'
>;
/** O que vem de fora da necessidade e decide a fase. */
export interface MicePhaseContext {
  /** As premissas do evento foram conferidas. */
  premisesChecked: boolean;
  /**
   * E-mails (minúsculos) com pedido direto de planilha ativo: a fila automática não pega a pronta
   * deles (`directOrderEmails` no mice-api). Sem o dado, vazio.
   */
  directOrderEmails?: ReadonlySet<string>;
}
/** O mínimo de um pedido para saber a fase do viajante. */
export type MicePhaseOrder = Pick<MiceOrderView, 'id' | 'status'>;

interface TravelerGroup<N, O> {
  id: string;
  need: N | null;
  orders: O[];
}

/** Junção: cada necessidade com os seus pedidos vivos, e um grupo por pedido que nenhuma referencia. */
function groupTravelers<N extends MicePhaseNeed, O extends MicePhaseOrder>(
  needs: readonly N[],
  orders: readonly O[],
): TravelerGroup<N, O>[] {
  const byId = new Map(orders.map((o) => [o.id, o]));
  const claimed = new Set<string>();
  const fromNeeds = needs.map((need) => {
    const mine = need.orderIds.flatMap((id) => {
      const order = byId.get(id);
      if (!order) return [];
      claimed.add(id);
      return isHistory(need, order) ? [] : [order];
    });
    return { id: need.id, need, orders: mine };
  });
  const loose = orders
    .filter((o) => !claimed.has(o.id))
    .map((o) => ({ id: `order:${o.id}`, need: null, orders: [o] }));
  return [...fromNeeds, ...loose];
}

const DIRECTION_ORDER: Record<MiceLegDirection, number> = { OUTWARD: 0, RETURN: 1, BOTH: 2 };

/**
 * Sentido de um pedido. O pedido gerado por necessidade traz o seu (`direction`); sem ele, a
 * origem decide (a da ida sai de uma das origens da necessidade).
 */
export function legDirection(need: MiceNeedView | null, order: MiceOrderView): MiceLegDirection {
  if (order.direction !== null) return order.direction;
  if (order.inbound !== null) return 'BOTH';
  if (!need) return 'OUTWARD';
  if (need.tripKind === 'return_only') return 'RETURN';
  if (need.tripKind === 'outbound_only') return 'OUTWARD';
  return order.outbound && need.outboundOrigins.includes(order.outbound.origin)
    ? 'OUTWARD'
    : 'RETURN';
}

/** Código do erro de uma necessidade/pedido parado por cadastro do viajante incompleto. */
export const PROFILE_INCOMPLETE_CODE = 'traveler_data_incomplete';

/**
 * O cadastro trava a compra. A checagem do cadastro manda quando existe; sem ela (validação que
 * não rodou, documento antigo), vale o código do erro.
 */
export const profileBlocked = (
  check: MiceProfileCheckView | null,
  error: { code: string } | null,
): boolean =>
  check === null ? error?.code === PROFILE_INCOMPLETE_CODE : profileNeedsAction(check);

/** Pronta em que o próprio agente falhou: pede o operador ("pedir de novo"), não outra rodada. */
const recommendationFailed = (need: MicePhaseNeed): boolean =>
  need.status === 'ready' &&
  (RECOMMENDATION_ERROR_CODES as readonly string[]).includes(need.error?.code ?? '');

/**
 * O agente pega sozinho: pronta, com as premissas conferidas, sem cadastro a resolver, sem uma
 * falha do próprio agente e sem pedido direto de planilha do mesmo e-mail (a fila o exclui).
 */
export const isAutoEligible = (need: MicePhaseNeed, ctx: MicePhaseContext): boolean =>
  need.status === 'ready' &&
  ctx.premisesChecked &&
  !profileBlocked(need.profileCheck, need.error) &&
  !recommendationFailed(need) &&
  !(ctx.directOrderEmails?.has(need.travelerEmail.toLowerCase()) ?? false);

const needsAttention = (
  need: MicePhaseNeed | null,
  statuses: readonly MiceOrderStatus[],
): boolean =>
  (need !== null && (ATTENTION_NEED.has(need.status) || recommendationFailed(need))) ||
  statuses.some((s) => ATTENTION_ORDER.has(s));

const allIssued = (need: MicePhaseNeed | null, statuses: readonly MiceOrderStatus[]): boolean =>
  statuses.length > 0 &&
  statuses.every((s) => s === 'issued') &&
  (need === null || need.status === 'ordered');

/** Com o agente: rodando ou à espera da próxima passada automática. */
const isAgentOwned = (need: MicePhaseNeed, ctx: MicePhaseContext): boolean =>
  need.status === 'recommending' || need.status === 'reconsidering' || isAutoEligible(need, ctx);

function phaseOf(
  need: MicePhaseNeed | null,
  orders: readonly MicePhaseOrder[],
  ctx: MicePhaseContext,
): MicePhase {
  const statuses = orders.map((o) => o.status);
  if (need?.status === 'manual') return 'manual';
  if (needsAttention(need, statuses)) return 'attention';
  if (statuses.some((s) => BUYING_ORDER.has(s))) return 'buying';
  if (allIssued(need, statuses)) return 'issued';
  if (need && isAgentOwned(need, ctx)) return 'agent';
  if (need && APPROVAL_NEED.has(need.status)) return 'approval';
  if (statuses.includes('ready')) return 'approval';
  return 'preparation';
}

function attentionAction(need: MiceNeedView | null, orders: readonly MiceOrderView[]) {
  if (orders.some((o) => o.status === 'indeterminate')) return 'verify';
  const blocked = orders.some(
    (o) =>
      (o.status === 'failed' || o.status === 'invalid') &&
      profileBlocked(need?.profileCheck ?? o.profileCheck, o.error),
  );
  if (blocked) return 'complete_profile';
  if (orders.some((o) => o.status === 'failed')) return 'retry';
  if (need && recommendationFailed(need)) {
    return profileBlocked(need.profileCheck, need.error) ? 'complete_profile' : 'recommend';
  }
  return 'details';
}

function approvalAction(
  need: MiceNeedView | null,
  orders: readonly MiceOrderView[],
): MiceNextAction {
  if (need?.status === 'no_solution') return 'reconsider';
  if (need?.status === 'approved' && profileBlocked(need.profileCheck, need.error)) {
    return 'complete_profile';
  }
  // Pedido de planilha pronto (sem necessidade): o cadastro dele decide antes da compra. Só o
  // pronto: um pedido morto com checagem antiga não decide pela compra de outro.
  if (
    need === null &&
    orders.some((o) => o.status === 'ready' && profileNeedsAction(o.profileCheck))
  ) {
    return 'complete_profile';
  }
  if (need?.status !== 'recommended') return 'buy';
  return need.recommendation?.color === 'green' ? 'approve' : 'review';
}

function nextActionOf(
  phase: MicePhase,
  need: MiceNeedView | null,
  orders: readonly MiceOrderView[],
): MiceNextAction {
  switch (phase) {
    case 'attention':
      return attentionAction(need, orders);
    case 'agent':
    case 'buying':
      return 'wait';
    case 'manual':
      return 'details';
    case 'approval':
      return approvalAction(need, orders);
    case 'preparation':
      if (need?.status !== 'ready') return 'wait';
      return profileBlocked(need.profileCheck, need.error) ? 'complete_profile' : 'recommend';
    case 'issued':
      return 'none';
    default: {
      const exhaustive: never = phase;
      return exhaustive;
    }
  }
}

function travelerOf(
  id: string,
  need: MiceNeedView | null,
  orders: readonly MiceOrderView[],
  ctx: MicePhaseContext,
): MiceTraveler {
  const first = orders[0];
  const phase = phaseOf(need, orders, ctx);
  return {
    id,
    rowNumber: need?.rowNumber ?? first?.rowNumber ?? 0,
    name: need?.travelerName ?? first?.travelerName ?? null,
    email: need?.travelerEmail ?? first?.travelerEmail ?? '',
    need,
    orders: orders
      .map((order) => ({ order, direction: legDirection(need, order) }))
      .sort((a, b) => DIRECTION_ORDER[a.direction] - DIRECTION_ORDER[b.direction]),
    phase,
    nextAction: nextActionOf(phase, need, orders),
  };
}

/** Um viajante por necessidade (com os pedidos dela) e um por pedido de planilha solto. */
export function buildTravelers(
  needs: readonly MiceNeedView[],
  orders: readonly MiceOrderView[],
  ctx: MicePhaseContext,
): MiceTraveler[] {
  return groupTravelers(needs, orders)
    .map((g) => travelerOf(g.id, g.need, g.orders, ctx))
    .sort((a, b) => a.rowNumber - b.rowNumber || a.id.localeCompare(b.id));
}

/** A fase de cada viajante, com a mesma junção de `buildTravelers`, a partir do mínimo. */
export function phasesOf(
  needs: readonly MicePhaseNeed[],
  orders: readonly MicePhaseOrder[],
  ctx: MicePhaseContext,
): MicePhase[] {
  return groupTravelers(needs, orders).map((g) => phaseOf(g.need, g.orders, ctx));
}

export function countByPhase(
  travelers: readonly Pick<MiceTraveler, 'phase'>[],
): Record<MicePhaseFilter, number> {
  const counts = Object.fromEntries(MicePhaseSchema.options.map((p) => [p, 0])) as Record<
    MicePhase,
    number
  >;
  for (const t of travelers) counts[t.phase] += 1;
  return { all: travelers.length, ...counts };
}

/** Minúsculas, sem acento e sem espaço: "Câmila" e "LA 3302" viram "camila" e "la3302". */
const fold = (value: string): string =>
  value
    .normalize('NFD')
    .replaceAll(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replaceAll(/\s+/g, '');

/** Voos do viajante (pedidos e recomendação) como "la3302". */
function flightsOf(traveler: MiceTraveler): string[] {
  const rec = traveler.need?.recommendation;
  const chosen = [rec?.outbound, rec?.inbound].flatMap((f) => f?.hops ?? []);
  const bought = traveler.orders.flatMap(({ order }) => [order.outbound, order.inbound]);
  return [...chosen, ...bought].flatMap((leg) =>
    leg ? [fold(`${leg.carrier}${leg.flightNumber}`)] : [],
  );
}

/** Busca por nome, e-mail ou voo, como texto literal (sem acento, caixa e espaço). */
export function matchesTravelerText(traveler: MiceTraveler, text: string): boolean {
  const q = fold(text);
  if (q === '') return true;
  const fields = [traveler.name ?? '', traveler.email].map(fold);
  return [...fields, ...flightsOf(traveler)].some((f) => f.includes(q));
}

/**
 * O cadastro do viajante pede ação: o da necessidade ou o de um pedido dele (a checagem da
 * necessidade manda no pedido dela, como na próxima ação). É o `profileBlocked`, que também conta o
 * aviso antigo sem checagem.
 */
function travelerProfilePending(traveler: MiceTraveler): boolean {
  const { need } = traveler;
  if (need && profileBlocked(need.profileCheck, need.error)) return true;
  return traveler.orders.some(({ order }) =>
    profileBlocked(need?.profileCheck ?? order.profileCheck, order.error),
  );
}

function matchesAlert(traveler: MiceTraveler, alert: MiceAlertFilter): boolean {
  if (alert === 'any') return true;
  if (alert === 'profile_pending') return travelerProfilePending(traveler);
  const codes = traveler.need?.recommendation?.alerts.map((a) => a.code) ?? [];
  return alert === 'none' ? codes.length === 0 : codes.includes(alert);
}

/** Origens da necessidade; sem ela, as dos pedidos de planilha. */
function originsOfOne(traveler: MiceTraveler): string[] {
  if (traveler.need) return traveler.need.outboundOrigins;
  return traveler.orders.flatMap(({ order }) => (order.outbound ? [order.outbound.origin] : []));
}

/** Cor da recomendação vigente do viajante; `null` sem recomendação (ou de versão antiga). */
const colorOf = (traveler: MiceTraveler): MiceColor | null =>
  traveler.need?.recommendation?.color ?? null;

/** Busca, alerta, origem e cor; a fase fica de fora (a barra conta o resultado disto por fase). */
export function filterTravelers(
  list: readonly MiceTraveler[],
  f: {
    q?: string;
    email?: string;
    alert: MiceAlertFilter;
    origin: string;
    color: MiceColor | 'any';
  },
): MiceTraveler[] {
  const email = f.email?.trim().toLowerCase();
  return list.filter(
    (t) =>
      (!email || t.email.toLowerCase() === email) &&
      matchesTravelerText(t, f.q ?? '') &&
      matchesAlert(t, f.alert) &&
      (f.color === 'any' || colorOf(t) === f.color) &&
      (f.origin === 'any' || originsOfOne(t).includes(f.origin)),
  );
}

/** IATA de origem únicas, ordenadas. */
export function originsOf(list: readonly MiceTraveler[]): string[] {
  return [...new Set(list.flatMap(originsOfOne))].sort(byCodeUnit);
}

/** Quantos viajantes de cada cor estão em "Para emitir" (as outras fases não têm cor a decidir). */
export function countByColor(
  travelers: readonly Pick<MiceTraveler, 'phase' | 'need'>[],
): Record<MiceColor, number> {
  const counts: Record<MiceColor, number> = { green: 0, yellow: 0, red: 0 };
  for (const t of travelers) {
    const color = t.need?.recommendation?.color ?? null;
    if (t.phase === 'approval' && color !== null) counts[color] += 1;
  }
  return counts;
}
